import {
  Box,
  Button,
  Grid,
  FormControl,
  FormLabel,
  TextField,
  CircularProgress,
  Alert,
  AlertTitle,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
} from "@mui/material";
import { ErrorMessage, Form, Formik, FormikProps } from "formik";
import { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import {
  addSetupType,
  addShopifyUrl,
  addSiteCredentials,
  useGetShopifyInstallMutation,
} from "../../../redux/reducer/features/Integration/DmsSetupSlice";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { AppState } from "../../../redux/reducer/rootReducer";
import { SetupType } from "../../../models/features/Integration/DmsSetup";

interface ShopifyInstallationProps {
  handleNext: any;
}

export const ShopifyValidationSchema = Yup.object().shape({
  shopUrl: Yup.string()
    .matches(
      /^(https:\/\/[a-zA-Z0-9][a-zA-Z0-9-]*\.myshopify\.com)(?!\/)$/,
      "Please enter a valid URL with this pattern https://XXXXXXXXX.myshopify.com"
    )
    .required("shopUrl is a required field"),
  clientId: Yup.string().required("Client Id is a required field"),
  clientSecret: Yup.string().required("Secret is a required field"),
});

export const ShopifyInstallation: React.FunctionComponent<
  ShopifyInstallationProps
> = ({ handleNext }) => {
  const dispatch = useDispatch();
  const dmsDealerId = useSelector(
    (state: AppState) => state.authReducer.dmsDealerId
  );

  const shopifyAccessToken = useSelector(
    (state: AppState) => state.dmsReducer.shopifyAccessToken
  );

  const formRef = useRef<FormikProps<any>>(null);
  const [installUrl, setInstallUrl] = useState<string>("");
  const [copySuccess, setCopySuccess] = useState(false);

  const [installationStatus, setInstallationStatus] = useState({
    isProcessed: false,
    isValid: false,
    errorMessage: "",
  });

  const errorMessage = "Failed to generate installation link. Please check your credentials and try again.";

  const [installationMutation, installationMutationResult] =
    useGetShopifyInstallMutation();

  useEffect(() => {
    if (installationMutationResult.isSuccess) {
      const data = installationMutationResult.data;
      if (data.status === 200 && data.entity) {
        setInstallUrl(data.entity);
        dispatch(addShopifyUrl({ url: formRef.current?.values?.shopUrl }));
        setInstallationStatus({
          isProcessed: true,
          isValid: true,
          errorMessage: "",
        });
      } else {
        setInstallUrl("");
        setInstallationStatus({
          isProcessed: true,
          isValid: false,
          errorMessage: data.message || errorMessage,
        });
      }
    }
    if (installationMutationResult.isError) {
      setInstallUrl("");
      setInstallationStatus({
        isProcessed: true,
        isValid: false,
        errorMessage: errorMessage,
      });
    }
  }, [installationMutationResult, dispatch]);

  const handleInstallation = (values: any) => {
    let siteCredentials = {
      clientId: values.clientId,
      clientSecret: values.clientSecret,
      shopUrl: values.shopUrl,
    };
    dispatch(addSetupType({ setupType: SetupType.Shopify }));
    dispatch(
      addSiteCredentials({ credentials: JSON.stringify(siteCredentials) })
    );
    installationMutation({
      shopUrl: values.shopUrl,
      clientId: values.clientId,
      clientSecret: values.clientSecret,
      dmsDealerId: dmsDealerId || "",
    });
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(installUrl);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "flex-end", p: 0.5, mt: 2 }}>
        <Button
          size="small"
          variant="contained"
          onClick={handleNext}
          disabled={
            !(installationStatus.isProcessed && installationStatus.isValid) ||
            !shopifyAccessToken
          }
        >
          Next
        </Button>
      </Box>

      <Grid container style={{ justifyContent: "center" }}>
        <Grid item xs={12} sm={6}>
          <Formik
            initialValues={{ shopUrl: "", clientId: "", clientSecret: "" }}
            enableReinitialize
            validationSchema={ShopifyValidationSchema}
            onSubmit={(values) => {
              handleInstallation(values);
            }}
            innerRef={formRef}
          >
            {({ values, handleChange, handleBlur }) => (
              <Form>
                <FormControl
                  sx={{ m: 3 }}
                  component="fieldset"
                  variant="standard"
                  style={{ alignItems: "center" }}
                >
                  <FormLabel component="h2" sx={{ fontSize: "1.5rem", mb: 3 }}>
                    Shopify Installation
                  </FormLabel>

                  <Grid container spacing={3} p={2}>
                    {/* Status Alert */}
                    <Grid item sm={12}>
                      {installationStatus.isProcessed && (
                        <Alert
                          severity={
                            installationStatus.isValid ? "success" : "error"
                          }
                        >
                          {installationStatus.isValid
                            ? "Installation link generated successfully!"
                            : installationStatus.errorMessage}
                        </Alert>
                      )}
                    </Grid>

                    {/* Form Fields */}
                    <Grid item sm={12}>
                      <TextField
                        id="clientId"
                        name="clientId"
                        label="App Client Id"
                        fullWidth
                        variant="standard"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.clientId}
                        disabled={installationStatus.isValid}
                        helperText={<ErrorMessage name="clientId" />}
                        error={
                          !!formRef.current?.errors?.clientId &&
                          !!formRef.current?.touched?.clientId
                        }
                      />
                    </Grid>
                    <Grid item sm={12}>
                      <TextField
                        id="clientSecret"
                        name="clientSecret"
                        label="App Secret"
                        type="password"
                        fullWidth
                        variant="standard"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.clientSecret}
                        disabled={installationStatus.isValid}
                        helperText={<ErrorMessage name="clientSecret" />}
                        error={
                          !!formRef.current?.errors?.clientSecret &&
                          !!formRef.current?.touched?.clientSecret
                        }
                      />
                    </Grid>
                    <Grid item sm={12}>
                      <TextField
                        id="shopUrl"
                        name="shopUrl"
                        label="Shopify Shop URL"
                        fullWidth
                        variant="standard"
                        placeholder="https://your-store.myshopify.com"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.shopUrl}
                        disabled={installationStatus.isValid}
                        helperText={<ErrorMessage name="shopUrl" />}
                        error={
                          !!formRef.current?.errors?.shopUrl &&
                          !!formRef.current?.touched?.shopUrl
                        }
                      />
                    </Grid>
                    <Grid item sm={12}>
                      <Button
                        variant="contained"
                        type="submit"
                        size="small"
                        disabled={
                          installationMutationResult.isLoading ||
                          installationStatus.isValid
                        }
                        startIcon={
                          installationMutationResult.isLoading ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : null
                        }
                      >
                        {installationMutationResult.isLoading
                          ? "Generating Link..."
                          : "Generate Installation Link"}
                      </Button>
                    </Grid>
                    {/* Installation Instructions */}
                    {installUrl && (
                      <Grid item sm={12}>
                        <Alert severity="info" sx={{ mt: 2 }}>
                          <AlertTitle>Installation Instructions</AlertTitle>

                          <List dense>
                            <ListItem>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <Typography variant="body2" fontWeight="bold">
                                  1.
                                </Typography>
                              </ListItemIcon>
                              <ListItemText primary="Log into your Shopify admin with administrator privileges" />
                            </ListItem>

                            <ListItem>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <Typography variant="body2" fontWeight="bold">
                                  2.
                                </Typography>
                              </ListItemIcon>
                              <ListItemText primary="Click 'Open Installation Link' to launch Shopify" />
                            </ListItem>

                            <ListItem>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <Typography variant="body2" fontWeight="bold">
                                  3.
                                </Typography>
                              </ListItemIcon>
                              <ListItemText primary="Review permissions and click 'Install' in Shopify" />
                            </ListItem>

                            <ListItem>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <Typography variant="body2" fontWeight="bold">
                                  4.
                                </Typography>
                              </ListItemIcon>
                              <ListItemText primary="After installation, click 'Next' above to continue" />
                            </ListItem>
                          </List>

                          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                            <Button
                              variant="contained"
                              startIcon={<OpenInNewIcon />}
                              onClick={() => window.open(installUrl, "_blank")}
                            >
                              Open Installation Link
                            </Button>

                            <Button
                              variant="outlined"
                              startIcon={
                                copySuccess ? (
                                  <CheckCircleIcon />
                                ) : (
                                  <ContentCopyIcon />
                                )
                              }
                              onClick={handleCopyLink}
                              color={copySuccess ? "success" : "primary"}
                            >
                              {copySuccess ? "Copied!" : "Copy Link"}
                            </Button>
                          </Stack>
                        </Alert>
                      </Grid>
                    )}
                  </Grid>
                </FormControl>
              </Form>
            )}
          </Formik>
        </Grid>
      </Grid>
    </>
  );
};
